/**
 * useMetricRegistry — typed view onto the canonical METRIC_DEFINITIONS
 * keyed by `MetricId`.
 *
 * Per-consumer convenience wrapper around the W3 substrate at
 * `@src/types/speedtest.ts`. Two call patterns:
 *
 *   - `all`     — the full Record (4 metrics) for callers that index by id.
 *   - `visible` — array filtered through `useVariantStore().variant.visibleMetrics`,
 *                 so dpi (download + upload only) and the default variant
 *                 (4 metrics) compute correctly without per-consumer filters.
 *
 * `visible` is a `ComputedRef<readonly MetricDef[]>` so v-for + filter
 * sites read it directly. The order matches `METRIC_IDS` (ping, jitter,
 * download, upload), which matches the chassis phase progression.
 *
 * Replaces three pre-T duplicates: `MetricPillCluster.metricDefs`,
 * `SpeedtestResults.metrics`, and the chart-side METRIC_LABELS read.
 *
 * AC.W5d.T3 / B2 §C7: the prior `byId(id)` lookup retired — `all[id]`
 * is the same indexed read with one fewer surface to maintain. Zero
 * consumer call sites (verified by grep at carve time).
 */

import { computed, type ComputedRef } from "vue";

import {
    METRIC_IDS,
    METRIC_DEFINITIONS,
    type MetricDef,
} from "@src/types/speedtest.ts";
import { useVariantStore } from "@src/stores/useVariantStore.ts";

export interface UseMetricRegistry {
    /** The canonical Record — every consumer reads label, abbr, color, etc. through this. */
    all: typeof METRIC_DEFINITIONS;
    /** Visible-metric subset filtered through the active variant. */
    visible: ComputedRef<readonly MetricDef[]>;
}

export function useMetricRegistry(): UseMetricRegistry {
    const variantStore = useVariantStore();

    const visible = computed<readonly MetricDef[]>(() =>
        METRIC_IDS
            .filter((id) => variantStore.variant.visibleMetrics.has(id))
            .map((id) => METRIC_DEFINITIONS[id]),
    );

    return {
        all: METRIC_DEFINITIONS,
        visible,
    };
}
